<template>
  <PageWrap
    wide
    :heading="$t('nav.integrations')"
    @updated="onUpdated"
  >
    <div
      :class="[
        $style.base,
        {
          [$style.smallScreen]: $screen === 's'
        }
      ]"
    >
      <FiskalyModal
        v-if="showFiskalyModal"
        @close="showFiskalyModal = false"
      />
      <BaseSpinner v-if="loading" />
      <div v-else>
        <div
          v-if="enabledApps.length"
          :class="$style.enabled"
        >
          <BaseHeading
            size="s"
            mb
          >
            {{ $t('global.enabled') }}
          </BaseHeading>
          <div :class="$style.apps">
            <AppCard
              v-for="(app, index) in enabledApps"
              :key="`${index}-enabled`"
              :app="app"
              v-test="`integration-${app.key}`"
            />
          </div>
        </div>
        <div v-if="disabledApps.length">
          <BaseHeading
            size="s"
            mb
          >
            {{ $t('global.disabled') }}
          </BaseHeading>
          <div :class="$style.apps">
            <AppCard
              v-for="(app, index) in disabledApps"
              :key="`${index}-disabled`"
              :app="app"
              v-test="`integration-${app.key}`"
              @enable="enableApp(app)"
            />
          </div>
        </div>
      </div>
    </div>
  </PageWrap>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import config from '@/config';
import { brandName, brandNamePay } from '@/helpers/brand-name';
import { flash } from '@/helpers/ui';
import { useAdyenStore } from '@/stores/adyen';
import { useCompanyStore } from '@/stores/company';
import { useNativeAppStore } from '@/stores/native-app';
import { useTreatwellStore } from '@/stores/treatwell';
import { useUserStore } from '@/stores/user';
import { OnlinePayment } from '@/types';
import unleash from '@/unleash';
import PageWrap from '../PageWrap.vue';
import AppCard from './AppCard.vue';
import FiskalyModal from './FiskalyModal.vue';
import {
  ENABLE_ADYEN,
  ENABLE_TREATWELL_MARKETPLACE,
  GET_APPS,
  GET_COMPANY_APPS,
} from './graphql';
import type { App } from './types';

const { t } = useI18n();
const { user } = useUserStore();

const showTwMarketplaceSettings = user?.salonizedAdmin || user?.impersonated;

const { isNativeApp } = useNativeAppStore();

const { company } = storeToRefs(useCompanyStore());

const defaultApps = ref<App[]>([
  ...(unleash.isEnabled('GiftyIntegration')
    ? [
        {
          key: 'gifty',
          enabled: false,
          heading: t('integrations.gifty.name'),
          description: t('integrations.gifty.description'),
          visual: '/img/integrations/gifty.svg',
          new: true,
          enableRoute: {
            name: 'integration-gifty',
          },
          settingsRoute: {
            name: 'integration-gifty',
          },
          externalLink: 'https://gifty.nl',
        },
      ]
    : []),
  {
    key: 'franchise',
    enabled: false,
    heading: t('integrations.franchise.name'),
    description: t('integrations.franchise.description'),
    visual: '/img/integrations/franchise.svg',
    visualInvertDarkTheme: true,
    settingsRoute: {
      name: 'integration-franchise',
    },
  },
  {
    key: 'google_calendar',
    enabled: false,
    heading: t('integrations.google_calendar.name'),
    description: t('integrations.google_calendar.description', brandName()),
    visual: '/img/integrations/google-calendar.svg',
    settingsRoute: {
      name: 'integration-google-calendar',
    },
    enablePath: '/auth/google',
  },
  {
    key: 'mailchimp',
    enabled: false,
    heading: t('integrations.mailchimp.name'),
    description: t('integrations.mailchimp.description'),
    visual: '/img/integrations/mailchimp.svg',
    visualInvertDarkTheme: true,
    enablePath: '/auth/mailchimp',
    settingsRoute: {
      name: 'integration-mailchimp',
    },
  },
  {
    key: 'mollie',
    enabled: false,
    heading: t('integrations.mollie.name'),
    description: t('integrations.mollie.description'),
    visual: '/img/integrations/mollie.svg',
    visualInvertDarkTheme: true,
    settingsRoute: {
      name: 'integration-mollie',
    },
    enablePath: '/auth/mollie',
    disableEnable: !!company.value.paymentGatewayProvider?.key,
  },
  {
    key: 'stripe',
    enabled: false,
    heading: t('integrations.stripe.name'),
    description: t('integrations.stripe.description'),
    visual: '/img/integrations/stripe.svg',
    visualInvertDarkTheme: true,
    enablePath: '/auth/stripe',
    disableEnable: !!company.value.paymentGatewayProvider?.key,
  },
  {
    key: 'treatwell',
    enabled: false,
    heading: t('integrations.treatwell.name'),
    visual: '/img/integrations/treatwell.svg',
    visualInvertDarkTheme: true,
    description: t('integrations.treatwell.description'),
    enablePath: '/admin/apps/treatwell/new',
  },
  {
    key: 'treatwell_marketplace',
    enabled: false,
    heading: t('integrations.treatwell_marketplace.name'),
    visual: '/img/integrations/treatwell.svg',
    visualInvertDarkTheme: true,
    description: t('integrations.treatwell_marketplace.description'),
    settingsRoute: showTwMarketplaceSettings
      ? {
          name: 'integration-treatwell-marketplace',
        }
      : undefined,
    readMoreLink: !showTwMarketplaceSettings ? 'treatwell-landing' : undefined,
  },
  {
    key: 'fiscalization',
    enabled: false,
    heading: t('integrations.fiskaly.name'),
    visual: '/img/integrations/fiskaly.svg',
    description: t('integrations.fiskaly.description'),
    disableUnavailable: true,
    alert: t('integrations.fiskaly.alert'),
  },
  {
    key: 'google_reserve',
    enabled: false,
    heading: t('integrations.google_reserve.name'),
    visual: '/img/integrations/rwg.svg',
    visualInvertDarkTheme: true,
    description: t('integrations.google_reserve.description', brandName()),
    disableUnavailable: true,
  },
  ...(unleash.isEnabled('SalonizedPayOnline')
    ? [
        {
          key: 'adyen',
          enabled: false,
          heading: t('integrations.adyen.name', brandNamePay()),
          visual: '/img/salonized-pay/logo.svg',
          description: t('integrations.adyen.description'),
          settingsRoute: {
            name: 'admin-online-bookings',
            query: { section: 'online-payments' },
          },
          disableEnable: !!company.value.paymentGatewayProvider?.key,
        },
      ]
    : []),
  {
    key: 'fiskaly',
    enabled: false,
    heading: t('integrations.fiskaly_fr.card_name'),
    visual: '/img/integrations/fiskaly.svg',
    visualInvertDarkTheme: true,
    description: t('integrations.fiskaly_fr.card_description'),
    disableUnavailable: true,
    enableRoute: {
      name: 'integration-fiskaly',
    },
    settingsRoute: {
      name: 'integration-fiskaly-sign-info',
    },
  },
]);

const apps = ref<App[]>(
  isNativeApp
    ? defaultApps.value.filter((app) => app.key !== 'google_calendar')
    : defaultApps.value,
);

provide('apps', apps);
provide('defaultApps', defaultApps);

const enabledApps = computed(() => apps.value.filter((app) => app.enabled));
const disabledApps = computed(() => apps.value.filter((app) => !app.enabled));

const loading = ref(true);
const appsLoaded = ref(false);
provide('appsLoaded', appsLoaded);

const { onResult: onAppsResult, refetch: refetchApps } = useQuery(GET_APPS);
onAppsResult(({ data }) => {
  loading.value = true;
  apps.value = apps.value.filter(
    (app: App) => !!data.apps.find((a: { key: string }) => app.key === a.key),
  );

  const { onResult } = useQuery(GET_COMPANY_APPS);
  onResult(
    ({
      data: {
        currentUser: { company },
      },
    }) => {
      company.apps.forEach((companyApp: { key: string; state: string }) => {
        const app = apps.value.find((a: App) => a.key === companyApp.key);
        if (app && companyApp.state === 'active') {
          app.enabled = true;
        }
      });

      loading.value = false;
      appsLoaded.value = true;
    },
  );
});

const router = useRouter();
const showFiskalyModal = ref(false);

const { updateCompany } = useCompanyStore();
const { adyenAppState } = useAdyenStore();
const redirectToSalonizedPay = () => {
  if (
    !adyenAppState.verificationStatus ||
    adyenAppState.verificationStatus !== 'VALID'
  ) {
    router.push({
      name: 'salonized-pay',
      query: { redirect: 'settings' },
    });
  }
};

const enableApp = (app: App) => {
  if (app.key === 'franchise') {
    router.push({
      name: 'integration-franchise',
    });
  } else if (app.key === 'treatwell_marketplace') {
    const { treatwellStatus } = useTreatwellStore();
    if (treatwellStatus.hasVenue) {
      const { mutate } = useMutation(ENABLE_TREATWELL_MARKETPLACE);
      mutate({
        input: {},
      }).then(() => {
        app.enabled = true;
        flash(t('global.flash.integration_enabled'));
      });
    } else {
      router.push({
        name: 'treatwell-admin',
      });
    }
  } else if (app.key === 'fiscalization') {
    showFiskalyModal.value = true;
  } else if (app.key === 'adyen') {
    if (adyenAppState.verificationStatus === 'VALID') {
      const { mutate } = useMutation(ENABLE_ADYEN);
      mutate({
        input: {},
      }).then(() => {
        updateCompany({
          settings: { bookings: { onlinePayment: OnlinePayment.Optional } },
        });
        company.value.settings.bookings.onlinePayment = OnlinePayment.Optional;
        company.value.paymentGateway = true;
        app.enabled = true;
        flash(t('global.flash.integration_enabled'));

        const { refetchAdyenAppState } = useAdyenStore();
        refetchAdyenAppState();
      });
    } else {
      redirectToSalonizedPay();
    }
  } else if (app.key === 'google_reserve') {
    router.push({
      name: 'online-google-reserve',
    });
  } else if (app.enablePath) {
    window.location.href = `${config.backendUrl}${app.enablePath}`;
  }
};

const onUpdated = () => {
  const { getUser } = useUserStore();
  getUser().finally(() => {
    refetchApps();
  });
};
</script>

<style
  lang="scss"
  module
>
.enabled {
  margin-bottom: $spacing * 2;
}

.apps {
  .base:not(.smallScreen) & {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing;
  }

  & > * {
    .base:not(.smallScreen) & {
      width: 330px;
    }

    .base.smallScreen & {
      &:not(:last-child) {
        margin-bottom: $spacing;
      }
    }
  }
}
</style>
